<template>
  <v-container class="pt-7 pb-11">
    <div class="d-flex align-center justify-space-between mb-5">
      <h1 class="text-h4 mb-0">{{ 'notifications'|localize }}</h1>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" :aria-label="'notificationSettings'|localize" @click="openSettings">
            <v-icon>mdi-cog-outline</v-icon>
          </v-btn>
        </template>
        <span>{{ 'notificationSettings'|localize }}</span>
      </v-tooltip>
    </div>
    <div class="white elevation-2 rounded">
      <v-row class="my-0 mb-1">
        <v-col class="py-0">
          <v-tabs v-model="tab" @change="clearList();getNotifications()" background-color="transparent" class="grey--text text--lighten-1">
            <v-tab :ripple="false" href="#all" :disabled="loading" class="text-none">
              {{ 'all'|localize }}
              <span class="ml-2 grey--text">{{ counts.all }}</span>
            </v-tab>
            <v-tab :ripple="false" href="#unread" :disabled="loading" class="text-none">
              {{ 'unread'|localize }}
              <v-chip v-if="counts.unread > 0" x-small color="error" dark class="ml-2">{{ counts.unread }}</v-chip>
            </v-tab>
            <v-tab
              v-for="type in enabledTypesList"
              :key="type"
              :ripple="false"
              :href="'#' + type"
              :disabled="loading"
              class="text-none"
            >
              <v-icon left small :color="NOTIFICATION_TYPE_COLORS[type]">{{ NOTIFICATION_TYPE_ICONS[type] }}</v-icon>
              {{ type|localize }}
              <span class="ml-2 grey--text">{{ counts.byType[type] || 0 }}</span>
            </v-tab>
          </v-tabs>
          <v-divider/>
        </v-col>
      </v-row>
      <v-row class="ma-0 mb-2">
        <v-col cols="4">
          <v-text-field
            :background-color="search ? 'serenade' : ''"
            v-model.trim="search"
            prepend-inner-icon="mdi-magnify"
            outlined
            hide-details
            dense
            clearable
            :placeholder="'searchNotifications'|localize"
          />
        </v-col>
        <v-col cols="3">
          <v-menu
            ref="menuDate"
            v-model="menuDate"
            :close-on-content-click="false"
            :return-value.sync="filters.date"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :background-color="filters.date.length ? 'serenade' : ''"
                :placeholder="'dateRange'|localize"
                :value="dateRangeText"
                readonly
                dense
                hide-details
                outlined
                clearable
                v-on="on"
                @click:clear="filters.date = [];clearList();getNotifications()"
              />
            </template>
            <v-date-picker
              v-model="filters.date"
              no-title
              scrollable
              range
              @input="filters.date.length === 2 && $refs.menuDate.save(filters.date)"
              @change="clearList();getNotifications()"
            />
          </v-menu>
        </v-col>
        <v-col cols="auto" class="d-flex align-center flex-wrap">
          <v-chip
            v-for="preset in datePresets"
            :key="preset.key"
            small
            :outlined="!isActivePreset(preset)"
            :dark="isActivePreset(preset)"
            color="primary"
            class="mr-2"
            @click="applyDatePreset(preset)"
          >
            {{ preset.label }}
          </v-chip>
        </v-col>
        <v-col cols="auto" class="ml-auto d-flex">
          <v-btn v-if="search || filters.date.length" text color="primary" class="mr-2" @click="resetFilters();clearList();getNotifications()">
            <v-icon left>mdi-sync</v-icon>
            {{ 'resetFilters'|localize }}
          </v-btn>
          <v-btn text color="primary" :disabled="!unreadCount" :loading="markingAll" @click="markAllAsRead">
            <v-icon left>mdi-check-all</v-icon>
            {{ 'markAllAsRead'|localize }}
          </v-btn>
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="notificationsList"
        :loading="loading"
        loader-height="2"
        :sort-by="defaultSortBy"
        :sort-desc="defaultSortDesc"
        :options.sync="options"
        :server-items-length="totalItems"
        item-key="id"
        :item-class="rowClass"
        hide-default-footer
      >
        <template v-slot:item.type="{ item }">
          <v-avatar size="36" :color="NOTIFICATION_TYPE_COLORS[item.type]">
            <v-icon dark size="18">{{ NOTIFICATION_TYPE_ICONS[item.type] }}</v-icon>
          </v-avatar>
        </template>
        <template v-slot:item.title="{ item }">
          <div
            class="py-2 notification-cell"
            tabindex="0"
            role="button"
            :aria-label="notificationText(item).title"
            @click="openDetails(item)"
            @keydown.enter="openDetails(item)"
            @keydown.space.prevent="openDetails(item)"
          >
            <div :class="{'font-weight-bold': !item.read_at}">{{ notificationText(item).title }}</div>
            <div class="text-caption grey--text text-truncate" style="max-width: 420px">{{ notificationText(item).summary }}</div>
          </div>
        </template>
        <template v-slot:item.related_id="{ item }">
          <router-link v-if="isLinkable(item)" class="primary--text font-weight-medium" :to="detailsRoute(item)" @click.native="markRead(item)">#{{ item.related_id }}</router-link>
          <span v-else class="grey--text">{{ item.related_id }}</span>
        </template>
        <template v-slot:item.created_at="{ item }">
          <div>{{ formatReceivedAt(item.created_at) }}</div>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center justify-center">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :aria-label="(item.read_at ? 'markAsUnread' : 'markAsRead')|localize"
                  @click.stop="item.read_at ? markUnread(item) : markRead(item)"
                >
                  <v-icon size="20">{{ item.read_at ? 'mdi-email-open-outline' : 'mdi-email-outline' }}</v-icon>
                </v-btn>
              </template>
              <span>{{ (item.read_at ? 'markAsUnread' : 'markAsRead')|localize }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  :aria-label="'delete'|localize"
                  @click.stop="confirmDelete(item)"
                >
                  <v-icon size="20">mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ 'delete'|localize }}</span>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:no-data>
          <div v-if="error" class="text-center py-10">
            <v-icon size="40" color="error">mdi-alert-circle-outline</v-icon>
            <div class="text-body-2 grey--text mt-2">{{ 'notificationsLoadError'|localize }}</div>
            <v-btn text color="primary" class="mt-2" @click="getNotifications">
              <v-icon left small>mdi-refresh</v-icon>
              {{ 'retry'|localize }}
            </v-btn>
          </div>
          <div v-else class="text-center py-10">
            <v-icon size="40" color="silver-chalice">mdi-check-circle-outline</v-icon>
            <div class="text-body-2 grey--text mt-2">{{ 'emptyNotificationsTitle'|localize }}</div>
          </div>
        </template>
      </v-data-table>
      <v-pagination
        color="primary"
        v-model="currentPage"
        :length="totalPages"
        :total-visible="7"
        class="py-6"
        :disabled="loading"
        @input="notificationsList = [];getNotifications();"
      />
    </div>

    <v-dialog v-model="detailsDialog" width="500" overlay-color="black" :overlay-opacity="0.3" scrollable>
      <v-card v-if="selectedItem">
        <v-card-text class="pa-6 pb-0">
          <div class="d-flex align-center mb-4">
            <v-avatar size="48" :color="NOTIFICATION_TYPE_COLORS[selectedItem.type]" class="mr-3 flex-shrink-0">
              <v-icon dark size="26">{{ NOTIFICATION_TYPE_ICONS[selectedItem.type] }}</v-icon>
            </v-avatar>
            <div class="text-h6">{{ notificationText(selectedItem).title }}</div>
          </div>
          <!-- Full, localized breakdown of the structured event data (data.params), so the
               exact fields behind the localized list text are visible here. -->
          <v-simple-table dense class="notification-details mb-4">
            <template v-slot:default>
              <tbody>
                <tr v-if="selectedItem.related_id">
                  <td class="grey--text text-caption">{{ 'request'|localize }}</td>
                  <td>
                    <router-link v-if="isLinkable(selectedItem)" class="primary--text font-weight-medium" :to="detailsRoute(selectedItem)" @click.native="markRead(selectedItem)">#{{ selectedItem.related_id }}</router-link>
                    <span v-else>#{{ selectedItem.related_id }}</span>
                  </td>
                </tr>
                <tr v-for="row in detailParamRows" :key="row.key">
                  <td class="grey--text text-caption">{{ row.label }}</td>
                  <td>
                    <template v-if="row.clamp">
                      <div :class="{'notification-details__clamp': !expandedRows[row.key]}">{{ row.value }}</div>
                      <a
                        class="text-caption primary--text"
                        tabindex="0"
                        role="button"
                        @click="toggleRowExpanded(row.key)"
                        @keydown.enter="toggleRowExpanded(row.key)"
                        @keydown.space.prevent="toggleRowExpanded(row.key)"
                      >
                        {{ (expandedRows[row.key] ? 'showLess' : 'showMore')|localize }}
                      </a>
                    </template>
                    <template v-else>{{ row.value }}</template>
                  </td>
                </tr>
                <tr>
                  <td class="grey--text text-caption">{{ 'received'|localize }}</td>
                  <td>{{ formatReceivedAt(selectedItem.created_at) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div v-if="selectedItem.data && selectedItem.data.emailed" class="d-flex align-center success--text mb-4">
            <v-icon color="success" size="16" class="mr-2">mdi-email-check-outline</v-icon>
            <span class="text-caption">{{ 'alsoSentByEmail'|localize }}</span>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-6 pt-0">
          <v-row>
            <v-col>
              <v-btn color="primary" block outlined rounded :loading="markingUnread" @click="markSelectedUnread">
                {{ 'markAsUnread'|localize }}
              </v-btn>
            </v-col>
            <v-col v-if="isLinkable(selectedItem)">
              <v-btn color="primary" block depressed rounded :to="detailsRoute(selectedItem)">
                {{ 'open'|localize }} #{{ selectedItem.related_id }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="settingsDialog" width="600" overlay-color="black" :overlay-opacity="0.3">
      <v-card class="pa-6">
        <div class="text-h6 mb-4">{{ 'notificationSettings'|localize }}</div>
        <v-progress-linear v-if="settingsLoading" height="2" indeterminate class="mb-4"/>
        <v-simple-table v-else>
          <template v-slot:default>
            <thead>
              <tr>
                <th>{{ 'type'|localize }}</th>
                <th>{{ 'description'|localize }}</th>
                <th class="text-center">{{ 'show'|localize }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="type in typesList" :key="type">
                <td>
                  <v-chip small outlined :color="NOTIFICATION_TYPE_COLORS[type]">
                    <v-icon left small>{{ NOTIFICATION_TYPE_ICONS[type] }}</v-icon>
                    {{ type|localize }}
                  </v-chip>
                </td>
                <td class="text-body-2 grey--text text--darken-1">{{ ('notificationTypeDescriptions.' + type)|localize }}</td>
                <td class="text-center">
                  <v-switch v-model="draftPrefs[type]" hide-details color="primary" class="ma-0 d-inline-block" :aria-label="type|localize"/>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <v-row class="mt-4">
          <v-col>
            <v-btn color="primary" block outlined rounded :disabled="saveLoading" @click="settingsDialog = false">
              {{ 'cancel'|localize }}
            </v-btn>
          </v-col>
          <v-col>
            <v-btn color="primary" block depressed rounded :loading="saveLoading" :disabled="settingsLoading" @click="savePreferences">
              {{ 'save'|localize }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <SimpleDialog
      ref="deleteNotificationDialog"
      :title="'deleteNotificationTitle'|localize"
      :text="'deleteNotificationText'|localize"
      :accept-text="'delete'|localize"
      :decline-text="'cancel'|localize"
      @accept="deleteSelectedNotification"
    />
  </v-container>
</template>

<script>
  import localize from '@/filters/localize';
  import methods from '@/helpers/methods';
  import notificationsApi from '@/api/notificationsApi';
  import resolveNotificationText from '@/helpers/notificationText';
  import SimpleDialog from '@/components/common/SimpleDialog';
  import {NOTIFICATION_TYPE_ICONS, NOTIFICATION_TYPE_COLORS, NOTIFICATION_TYPES_BY_ROLE} from '@/enums/notificationTypes';

  export default {
    name: 'Notifications',

    components: {SimpleDialog},

    data() {
      return {
        NOTIFICATION_TYPE_ICONS,
        NOTIFICATION_TYPE_COLORS,
        tab: this.initialHash(),
        loading: true,
        error: false,
        markingAll: false,
        markingUnread: false,
        debouncedSearch: '',
        debounceTimeout: null,
        defaultSortBy: 'created_at',
        defaultSortDesc: true,
        options: {},
        itemsPerPage: 20,
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        // Tab-agnostic badge counts (All / Unread / per-type) from the list response; they
        // honour the active date/search filters, unlike the global bell count in the store.
        counts: {all: 0, unread: 0, byType: {}},
        notificationsList: [],
        menuDate: false,
        filters: {
          date: [],
        },
        detailsDialog: false,
        selectedItem: null,
        // Details dialog: per-row expanded state for long free-text params (row.key -> bool),
        // reset whenever a different notification is opened (see openDetails()).
        expandedRows: {},
        // Per-notification-id chain of in-flight read/unread promises (see
        // setNotificationReadState()) - not rendered, just bookkeeping.
        pendingReadRequests: {},
        notificationToDelete: null,
        settingsDialog: false,
        settingsLoading: false,
        saveLoading: false,
        prefs: {},
        // Draft copy of `prefs` the settings dialog's switches are bound to, so toggling
        // them doesn't change enabledTypesList/the type tabs until Save applies it to
        // `prefs` (see fetchPreferences()/savePreferences()) - Cancel just discards it.
        draftPrefs: {},
        prefsLoaded: false,
      };
    },

    mounted() {
      // Type tabs are only shown for types the user hasn't muted — fetch preferences once
      // on load (same call the settings dialog uses) so that list can be computed upfront.
      this.fetchPreferences();
    },

    beforeDestroy() {
      clearTimeout(this.debounceTimeout);
    },

    watch: {
      options: {
        handler() {
          this.clearList();
          this.getNotifications();
        },
        deep: true,
      },
      search() {
        this.clearList();
        this.getNotifications();
      },
    },

    computed: {
      userRole() {
        return this.$store.getters.userData && this.$store.getters.userData.role;
      },
      typesList() {
        return NOTIFICATION_TYPES_BY_ROLE[this.userRole] || [];
      },
      // Drives the type tabs: only the role's types that are also currently enabled in
      // the notification preferences (a muted type gets no tab). Empty until preferences
      // have loaded once, so a muted type never flashes a tab before disappearing.
      enabledTypesList() {
        if (!this.prefsLoaded) return [];
        return this.typesList.filter((type) => this.prefs[type] !== false);
      },
      // True when the active tab is a type tab (e.g. "quote") rather than "all"/"unread".
      isTypeTab() {
        return this.tab !== 'all' && this.tab !== 'unread';
      },
      unreadCount() {
        return this.$store.getters.unreadNotificationsCount;
      },
      headers() {
        // On a type tab every row shares that type, so the Type column just repeats the tab
        // and is dropped; on All/Unread it stays because the list mixes types.
        return [
          {text: localize('type'), value: 'type', width: 96},
          {text: localize('id'), value: 'related_id', sortable: false, width: 90},
          {text: localize('received'), value: 'created_at', width: 160},
          {text: localize('notification'), value: 'title', sortable: false},
          {text: localize('actions'), value: 'actions', sortable: false, width: 120, align: 'center'},
        ].filter((header) => header.value !== 'type' || !this.isTypeTab);
      },
      dateRangeText() {
        return [...this.filters.date].sort().join(' ~ ');
      },
      // Event params of the open notification, as localized label/value rows for the details
      // dialog. Fixed, readable order; only params that are actually present are shown.
      detailParamRows() {
        const params = (this.selectedItem && this.selectedItem.data && this.selectedItem.data.params) || {};
        const present = (value) => value !== undefined && value !== null && value !== '';
        // Price carries its own currency code (SEK/USD/EUR) from the source proposal when set.
        const price = present(params.price)
          ? (present(params.currency) ? `${params.price} ${params.currency}` : params.price)
          : null;
        // Free-text fields (carrier/booking side) can run long — flag them so the template
        // clamps to ~3 lines with a per-row Show full/Show less toggle (see expandedRows).
        const isLong = (value) => present(value) && String(value).length > 120;
        return [
          {key: 'request_title', label: localize('requestTitle'), value: params.request_title},
          {key: 'route', label: localize('route'), value: params.route},
          {key: 'company', label: localize('company'), value: params.company},
          {key: 'customer', label: localize('customer'), value: params.customer},
          {key: 'price', label: localize('price'), value: price},
          {key: 'transit', label: localize('estimatedTransitTime'), value: params.transit},
          {key: 'transit_comment', label: localize('transitComment'), value: params.transit_comment, clamp: isLong(params.transit_comment)},
          {key: 'offer_reference', label: localize('offerReference'), value: params.offer_reference},
          {key: 'valid_to', label: localize('validUntil'), value: params.valid_to},
          {key: 'note', label: localize('carrierNote'), value: params.note, clamp: isLong(params.note)},
          {key: 'booking_note', label: localize('bookingNote'), value: params.booking_note, clamp: isLong(params.booking_note)},
        ].filter((row) => present(row.value));
      },
      // Quick shortcuts for the date-range filter, computed off "today" (local time) so
      // the values line up with what the backend's whereDate(created_at, >=/<=) comparison
      // expects (YYYY-MM-DD, inclusive on both ends).
      datePresets() {
        const today = this.formatDateLocal(new Date());
        return [
          {key: 'today', label: localize('today'), range: [today, today]},
          {key: 'last7Days', label: localize('last7Days'), range: [this.dateDaysAgo(6), today]},
          {key: 'last30Days', label: localize('last30Days'), range: [this.dateDaysAgo(29), today]},
        ];
      },
      search: {
        get() {
          return this.debouncedSearch;
        },
        set(val) {
          if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
          this.debounceTimeout = setTimeout(() => {
            this.debouncedSearch = val;
          }, 500);
        },
      },
    },

    methods: {
      clearList() {
        this.notificationsList = [];
        this.totalPages = 0;
        this.totalItems = 0;
        this.currentPage = 1;
      },
      // Clears every active filter (type tab, date range, search) and returns to the
      // unfiltered "All" view — same resetFilters() pattern as AdminRequests.vue /
      // CustomerRequests.vue; the caller still chains clearList()/getNotifications().
      resetFilters() {
        this.tab = 'all';
        this.filters.date = [];
        // `search` is a debounced computed backed by `debouncedSearch` (see the computed
        // below) — clear the pending timeout and set the underlying value directly so the
        // field empties immediately instead of waiting out the 500ms debounce.
        clearTimeout(this.debounceTimeout);
        this.debouncedSearch = '';
      },
      // Local-time YYYY-MM-DD, matching what v-date-picker's own v-model produces and
      // what the backend's date_from/date_to filter expects — avoids the UTC shift of
      // Date#toISOString() near midnight.
      formatDateLocal(date) {
        const pad = (n) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      },
      dateDaysAgo(days) {
        const date = new Date();
        date.setDate(date.getDate() - days);
        return this.formatDateLocal(date);
      },
      applyDatePreset(preset) {
        this.filters.date = [...preset.range];
        this.clearList();
        this.getNotifications();
      },
      isActivePreset(preset) {
        if (this.filters.date.length !== 2) return false;
        let current = [...this.filters.date].sort();
        return current[0] === preset.range[0] && current[1] === preset.range[1];
      },
      rowClass(item) {
        return item.read_at ? '' : 'notification-row--unread';
      },
      notificationText(item) {
        return resolveNotificationText(item);
      },
      // The backend returns `created_at` as a raw ISO 8601 timestamp (e.g.
      // "2026-09-18T06:55:16.000000Z"), unlike the space-separated "Y-m-d H:i:s" strings
      // the shared `formatDate` filter expects from the older list endpoints — display
      // only, sorting still sends the untouched `created_at`/`-created_at` field.
      formatReceivedAt(isoString) {
        if (!isoString) return '';
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return isoString;
        const pad = (n) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
      },
      isLinkable(item) {
        return methods.isNotificationLinkable(item);
      },
      detailsRoute(item) {
        return methods.notificationDetailsRoute(this.userRole, item.related_id);
      },
      // The tab bar is now single-select and doubles as the type filter: "All" and
      // "Unread" request their own `tab` value with no type filter, while a type tab
      // (e.g. "quote") requests `tab=all` narrowed down with `type=[<that type>]`.
      queryData() {
        let query = {
          tab: this.isTypeTab ? 'all' : this.tab,
          page: this.currentPage,
          per_page: this.itemsPerPage,
        };
        this.options.sortBy && this.options.sortBy[0] && (query.sort = (this.options.sortDesc[0] ? '-' : '') + this.options.sortBy[0]);
        this.search && (query.search = this.search);
        this.isTypeTab && (query.type = [this.tab]);
        if (this.filters.date.length === 2) {
          let sortedDates = [...this.filters.date].sort();
          query.date_from = sortedDates[0];
          query.date_to = sortedDates[1];
        }
        return query;
      },
      async getNotifications() {
        try {
          this.loading = true;
          this.error = false;
          let response = await notificationsApi.getNotifications(this.queryData());
          this.notificationsList = response.data.data || [];
          this.totalItems = response.data.total || 0;
          this.totalPages = this.itemsPerPage ? Math.ceil(this.totalItems / this.itemsPerPage) : 0;
          // Every tab badge (All / Unread / per-type) comes from one tab-agnostic breakdown
          // that honours the active date/search filters, so no tab needs a separate count
          // request and the Unread badge matches the filtered list rather than the global
          // count.
          this.setCounts(response.data.counts);
          // `unread` is the user's global unread count, independent of the current
          // tab/filters — keep the store (bell badge) fresh without waiting for the poll.
          this.$store.commit('setUnreadNotificationsCount', response.data.unread || 0);
        } catch (e) {
          // Distinct from a genuinely empty inbox — the no-data slot checks `error` so a
          // failed fetch doesn't render as "you're all caught up".
          this.error = true;
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.loading = false;
        }
      },
      // Normalizes the response's `counts` breakdown into the reactive shape the tab badges
      // read (`by_type` -> `byType`), tolerating a missing/partial payload.
      setCounts(counts) {
        counts = counts || {};
        this.counts = {
          all: counts.all || 0,
          unread: counts.unread || 0,
          byType: counts.by_type || {}
        };
      },
      async markAllAsRead() {
        try {
          this.markingAll = true;
          await notificationsApi.markAllNotificationsRead();
          if (this.tab === 'unread') {
            // Every row on this tab just stopped matching the Unread filter — same end
            // state as clearList(), just reached via mark-all instead of a tab/filter change.
            this.clearList();
          } else {
            // Mutate in place (not map+spread into fresh objects) so a shared reference —
            // e.g. the details dialog's selectedItem, or a row an in-flight
            // setNotificationReadState() call still points at — stays in sync.
            const now = new Date().toISOString();
            this.notificationsList.forEach((item) => {
              item.read_at = item.read_at || now;
            });
          }
          // Nothing is unread anymore, regardless of the active tab/filter.
          this.counts.unread = 0;
          this.$store.dispatch('fetchUnreadNotificationsCount');
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.markingAll = false;
        }
      },
      openDetails(item) {
        this.selectedItem = item;
        this.expandedRows = {};
        this.detailsDialog = true;
        this.markRead(item);
      },
      toggleRowExpanded(key) {
        this.expandedRows = {...this.expandedRows, [key]: !this.expandedRows[key]};
      },
      // Single place the row toggle, the details dialog (markSelectedUnread) and the row's
      // Related deep-link all go through to flip a notification's read state. The row and
      // the details dialog's selectedItem share the same `item` reference, so mutating it
      // here keeps both in sync; on the Unread tab a row that just became read is also
      // dropped from the list locally (mirrors deleteSelectedNotification()), checked
      // against the live array rather than a flag so a later opposite call for the same
      // item can't double-decrement the counters.
      //
      // Calls for the same notification id are chained (not fired in parallel): openDetails()
      // fires markRead() automatically the instant the dialog opens, and a fast follow-up
      // click (e.g. the dialog's "mark as unread" button) used to race it over the network —
      // whichever response landed last silently won, regardless of which one was the user's
      // actual last click. Chaining runs them in click order, so the last action always wins.
      setNotificationReadState(item, isRead) {
        // Only short-circuit on the current read_at when nothing is pending for this item —
        // while a call is in flight, read_at is still the PRE-transition value, so this check
        // would otherwise wrongly treat an opposite call as "already there" and drop it.
        if (!this.pendingReadRequests[item.id] && !!item.read_at === isRead) return Promise.resolve();
        const prior = this.pendingReadRequests[item.id] || Promise.resolve();
        const run = prior.catch(() => {}).then(async () => {
          if (!!item.read_at === isRead) return; // already the target state by the time our turn comes up
          if (isRead) await notificationsApi.markNotificationRead(item.id);
          else await notificationsApi.markNotificationUnread(item.id);
          const wasListed = this.tab === 'unread' && this.notificationsList.includes(item);
          item.read_at = isRead ? new Date().toISOString() : null;
          // The toggled row is always within the active date/search scope (it's on screen),
          // so the filtered Unread badge moves by exactly one in the transition's direction.
          this.counts.unread = isRead ? Math.max(0, this.counts.unread - 1) : this.counts.unread + 1;
          if (isRead && wasListed) {
            this.notificationsList = this.notificationsList.filter((n) => n !== item);
            this.totalItems = Math.max(0, this.totalItems - 1);
            this.totalPages = this.itemsPerPage ? Math.ceil(this.totalItems / this.itemsPerPage) : 0;
          } else if (!isRead && this.tab === 'unread' && !this.notificationsList.includes(item)) {
            // Symmetric case: this same item was already dropped from THIS Unread-tab list by
            // an earlier read transition (e.g. openDetails' automatic markRead racing this
            // unread click) - put it back so the list and its counters keep matching what's
            // genuinely unread, instead of leaving a "phantom" gap until the next refetch.
            this.notificationsList.unshift(item);
            this.totalItems += 1;
            this.totalPages = this.itemsPerPage ? Math.ceil(this.totalItems / this.itemsPerPage) : 0;
          }
          this.$store.dispatch('fetchUnreadNotificationsCount');
        }).finally(() => {
          if (this.pendingReadRequests[item.id] === run) delete this.pendingReadRequests[item.id];
        });
        this.pendingReadRequests[item.id] = run;
        return run;
      },
      // Shared by the row/dropdown click, the quick mark-as-read button and the Related
      // deep-link click.
      async markRead(item) {
        try {
          await this.setNotificationReadState(item, true);
        } catch (e) {}
      },
      // Row-level counterpart to markRead(), used by the Actions column toggle.
      async markUnread(item) {
        try {
          await this.setNotificationReadState(item, false);
        } catch (e) {}
      },
      async markSelectedUnread() {
        try {
          this.markingUnread = true;
          await this.setNotificationReadState(this.selectedItem, false);
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.markingUnread = false;
        }
      },
      confirmDelete(item) {
        this.notificationToDelete = item;
        this.$refs.deleteNotificationDialog.open();
      },
      async deleteSelectedNotification() {
        if (!this.notificationToDelete) return;
        const item = this.notificationToDelete;
        try {
          await notificationsApi.deleteNotification(item.id);
          this.notificationsList = this.notificationsList.filter((n) => n.id !== item.id);
          this.totalItems = Math.max(0, this.totalItems - 1);
          this.totalPages = this.itemsPerPage ? Math.ceil(this.totalItems / this.itemsPerPage) : 0;
          // Keep every tab badge in step with the removed row: All always drops by one, its
          // type bucket drops by one, and Unread only if the row was still unread.
          this.counts.all = Math.max(0, this.counts.all - 1);
          if (this.counts.byType[item.type] != null) {
            this.counts.byType[item.type] = Math.max(0, this.counts.byType[item.type] - 1);
          }
          if (!item.read_at) {
            this.counts.unread = Math.max(0, this.counts.unread - 1);
          }
          this.$store.dispatch('fetchUnreadNotificationsCount');
          methods.showGlobalSnackbar('success', localize('done'));
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.notificationToDelete = null;
        }
      },
      initialHash() {
        const queryHash = this.$router.currentRoute.hash.slice(1);
        return queryHash === 'unread' ? 'unread' : 'all';
      },
      // Shared by the mount-time fetch (drives the type tabs) and the settings dialog
      // (drives the mute switches) — both need the same {type: enabled} map.
      async fetchPreferences() {
        this.prefs = {};
        try {
          let response = await notificationsApi.getNotificationPreferences();
          (response.data.data || []).forEach((item) => {
            if (this.typesList.includes(item.type)) this.$set(this.prefs, item.type, item.enabled);
          });
          // Reset the settings dialog's draft to the applied state (a stale draft from a
          // previous open/Cancel must never leak into the switches).
          this.draftPrefs = {...this.prefs};
          this.prefsLoaded = true;
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        }
      },
      async openSettings() {
        this.settingsDialog = true;
        try {
          this.settingsLoading = true;
          await this.fetchPreferences();
        } finally {
          this.settingsLoading = false;
        }
      },
      async savePreferences() {
        try {
          this.saveLoading = true;
          let preferences = {};
          this.typesList.forEach((type) => {
            preferences[type] = !!this.draftPrefs[type];
          });
          await notificationsApi.updateNotificationPreferences({preferences});
          // Only now does the draft become the applied state that enabledTypesList/the type
          // tabs read - Cancel (settingsDialog = false with no further action) never reaches
          // this line, so it leaves `prefs` untouched.
          this.prefs = {...this.draftPrefs};
          methods.showGlobalSnackbar('success', localize('done'));
          this.settingsDialog = false;
          // The active tab may be a type that was just muted — it no longer has a tab of
          // its own, so fall back to "All".
          if (this.isTypeTab && !this.enabledTypesList.includes(this.tab)) {
            this.tab = 'all';
          }
          // A muted type must disappear from both the list and the unread badge right away
          // (server-side read gate), not just on the next poll/reload.
          this.getNotifications();
          this.$store.dispatch('fetchUnreadNotificationsCount');
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.saveLoading = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .notification-cell {
    cursor: pointer;
  }

  // Details dialog table: narrow label column, value wraps instead of forcing a wide dialog.
  .notification-details ::v-deep td {
    height: auto;
    padding: 8px 0;
    vertical-align: middle;
    word-break: break-word;
  }

  .notification-details ::v-deep td:first-child {
    width: 130px;
    white-space: nowrap;
    padding-right: 12px;
  }

  .notification-details__clamp {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  // Same reasoning as NotificationsDropdown.vue: Vuetify's custom theme colors are only
  // resolved through component props at runtime, so this rule uses the literal hex.
  ::v-deep tr.notification-row--unread {
    background-color: #fff1e5;
    font-weight: 600;
  }

  ::v-deep tr.notification-row--unread > td:first-child {
    border-left: 3px solid #fc8101;
  }
</style>
